import React, { useEffect, useState, useContext } from 'react';
import { withStyles } from '@material-ui/core/styles';
import themeCreateInjectableStyles from 'Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import getTimezoneOffset from 'Support/DateTime/GetTimezoneOffset/DateTimeGetTimezoneOffset.support';
import cn from 'classnames';
import { ORDER_EDIT_INFO_ALREADY_CONFIRMED_ON_THIS_DEVICE } from 'Constants/Order/Order.constants';
import ServicesContext from 'Contexts/Services/Services.context';
import ModifyingInfoModal from 'Components/EditOrder/comps/ModifyingInfoModal.component';
import ModalComponent from 'Components/Modal/Modal.component';
import PageTip from 'Components/PageTip/PageTip.component';
import { Typography, FormControl, Input, InputAdornment, FormHelperText, Grid, Button } from '@material-ui/core';
import Alert from '../parts/comps/Alert';
import hitGTM from 'State/Store/UI/Actions/GTM.action';
import {
  UI_PLUS_LOAD_FORM_DIALOG_CALL_BORAL_LINK,
  UI_ON_SUBMIT_BUTTON_CLICK,
  UI_ON_FINISH_AND_REVIEW_BUTTON_CLICK,
  UI_ON_FINISH_BUTTON_CLICK,
  UI_ON_BACK_ARROW_BUTTON_CLICK,
  UI_ON_BACK_BUTTON_CLICK,
} from 'State/Store/UI/UI.types';
import { useDispatch } from 'react-redux';
import EditOrderStep2, { EditOrderForm } from './parts/Step2.EditOrderForm';
import { Order } from 'types/Order';
import moment from 'moment';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditOrderStep3, { EditOrderChangingItem } from './parts/Step3.Review';
import EditOrderModalStyles from './EditOrderModal.styles';
import { Nullable } from 'types/utils/Nullable';
import {
  resolveDateTimeByReplacingDotWithColon,
  resolveDateTimeOmittingTimeZoneDesignator,
  isSameTime,
  isSameDate,
  getDateTimeDetails,
} from './utils/resolveDateTime';
import { getTestDetailsFromInstructions } from './utils/getTestDetailsFromInstructions';
import uiTriggered from 'State/Store/UI/Actions/UI.Triggered.action';
import PleaseWatiSpinner from '../parts/comps/PleaseWatiSpinner';
import ProgressDone from '../parts/comps/ProgressDone';
import { SNACKBAR_GENERAL_ERROR_MESSAGE, openSnackbar } from 'State/Store/Snackbar/SnackbarActionCreators';
import { Services } from 'types/Services';
import { UpdateOrderServicePayload } from 'Services/Order/services/update-order';
import Icons from '../../Icons/Icons.component';
import Paper from '@material-ui/core/Paper';
import * as themeConstants from '../../../Constants/Theme/Theme.constants';
import { ORDER_API_DATE_FORMAT, ORDER_STATUS_CANCELLED, ORDER_STATUS_CONFIRMED } from '../../../Constants/Order/Order.constants';
import dateOnly from '../../../Support/DateTime/DateOnly/DateTimeDateOnly.support';

interface Props {
  open: boolean;
  order: Order;
  onClose(): void;
  onGoToThisOrder(newOrderKey?: string): void;
  onGoToOrders(): void;
  classes: any;
}

enum Status {
  ALERT,
  INITIAL,
  CONFIRM,
  PROCESSING,
  DONE,
}

const EditOrderModalComp = (props: Props) => {
  const { open, order, onClose, onGoToThisOrder, onGoToOrders, classes } = props;
  const dispatch = useDispatch();
  const services: Services = useContext(ServicesContext);
  const [initialized, setInitialized] = useState(false);
  const [status, setStatus] = useState<Status>(Status.ALERT);
  const [orderForm, setOrderForm] = useState<Nullable<EditOrderForm>>({
    startTime: null,
    startDate: null,
    instructions: null,
    quantity: null,
    siteTel: null,
    isDateTimeCombinationValid: null,
    purchaseOrder: null,
  });
  const [isSamePhoneNumber, setSamePhoneNumber] = useState(true);
  const [isPhoneNumberValid, setPhoneNumberValid] = useState(true);
  const [purchaseOrderRequired, setPurchaseOrderRequired] = useState(false);
  const [newOrderKey, setNewOrderKey] = useState<string | undefined>(undefined);

  useEffect(() => {
    const checkDontShowAgain = async () => {
      const result = await services.storageService.getItem(ORDER_EDIT_INFO_ALREADY_CONFIRMED_ON_THIS_DEVICE);
      setInitialized(true);
      if (result) {
        setStatus(Status.INITIAL);
      }
    };
    checkDontShowAgain();

    setOrderForm({
      instructions: order.details!.instructions ?? '',
      quantity: order.details ? order.details.qty.ordered : 0,
      siteTel: order.details!.siteTel,
      startDate: resolveDateTimeByReplacingDotWithColon(order.summary.startTime),
      startTime: resolveDateTimeOmittingTimeZoneDesignator(resolveDateTimeByReplacingDotWithColon(order.summary.startTime)),
      purchaseOrder: order.details?.po ?? '',
    });
  }, []);

  if (!initialized) {
    return null;
  }
  if (!order.details) {
    return null;
  }

  const checkInstructionsChanging = (orderFormInstructions: string | null, detailInstructions: string | null) => {
    const finalA = orderFormInstructions === null ? '' : orderFormInstructions;
    const finalB = detailInstructions === null ? '' : detailInstructions;
    return finalA !== finalB;
  };

  const isSiteTelChanging = checkInstructionsChanging(orderForm.siteTel, order.details.siteTel);
  const isInstructionsChanging = checkInstructionsChanging(orderForm.instructions, order.details.instructions);
  const isInstructionsValid = orderForm.instructions !== null ? orderForm.instructions.length <= 272 : false;
  const isPurchaseOrderChanging = checkInstructionsChanging(orderForm.purchaseOrder, order.details.po);
  const isQuantityValid = orderForm && orderForm.quantity && orderForm.quantity > 0 && orderForm.quantity <= 2000;
  const poLength = orderForm.purchaseOrder?.trim()?.length ?? 0;
  const isPurchaseOrderValid = poLength <= 24 && (poLength > 0 || !purchaseOrderRequired);
  const isQuantityChanging = orderForm.quantity !== order.details.qty.ordered;
  const isStartTimeChanging = !isSameTime(
    resolveDateTimeOmittingTimeZoneDesignator(resolveDateTimeByReplacingDotWithColon(order.summary.startTime)),
    orderForm.startTime,
  );
  const isStartDateChanging = !isSameDate(
    resolveDateTimeOmittingTimeZoneDesignator(resolveDateTimeByReplacingDotWithColon(order.summary.startTime)),
    orderForm.startTime,
  );
  const isDateTimeCombinationValid = typeof orderForm.isDateTimeCombinationValid === 'undefined' ? true : orderForm.isDateTimeCombinationValid;

  const isSomeFieldChanging =
    isSiteTelChanging || isInstructionsChanging || isQuantityChanging || isStartTimeChanging || isStartDateChanging || isPurchaseOrderChanging;

  const isAllFieldsValid = isPhoneNumberValid && isInstructionsValid && isDateTimeCombinationValid && isQuantityValid && isPurchaseOrderValid;
  const submittable = isSomeFieldChanging && isAllFieldsValid;
  // console.log(isInstructionsValid, isInstructionsChanging, orderForm.instructions, order.details.instructions)

  const changingItems: EditOrderChangingItem[] = [];
  if (isSiteTelChanging) {
    changingItems.push({
      fieldKey: 'siteTel',
      label: `SMS Notification Number`,
      formattedValue: orderForm.siteTel || '',
    });
  }
  if (isPurchaseOrderChanging) {
    changingItems.push({
      fieldKey: 'purchaseOrder',
      label: `Purchase Order Number`,
      formattedValue: orderForm.purchaseOrder || '',
    });
  }
  if (isStartTimeChanging) {
    changingItems.push({
      fieldKey: 'startTime',
      label: `Start Time`,
      formattedValue: moment(orderForm.startTime).format(`h:mmA`),
    });
  }
  if (isStartDateChanging) {
    changingItems.push({
      fieldKey: 'startTime',
      label: `Start Date`,
      formattedValue: moment(orderForm.startTime).format(`dddd D MMMM`),
      tip: (
        <PageTip
          content={
            <Typography variant="body2" className={classes.toolTipContent}>
              The modified order will be moved to its new date with a new order number. The old order will appear as cancelled.
            </Typography>
          }
        />
      ),
    });
  }
  if (isInstructionsChanging) {
    changingItems.push({
      fieldKey: 'instructions',
      label: `Driver Instructions`,
      formattedValue: orderForm.instructions || '',
    });
  }
  if (isQuantityChanging) {
    changingItems.push({
      fieldKey: 'quantity',
      label: `Quantity`,
      formattedValue: `${orderForm.quantity || 0} m³`,
    });
  }

  const getParts = (): {
    title: null | React.ReactNode;
    content: null | React.ReactNode;
    footer: null | React.ReactNode;
    titleIcon?: null | React.ReactNode;
    rightHeading?: null | React.ReactNode;
  } => {
    switch (status) {
      case Status.INITIAL:
        return getInitialParts();
      case Status.CONFIRM:
        return getConfirmParts();
      case Status.PROCESSING:
        return getProgressingParts();
      case Status.DONE:
        return getDoneParts();
      default:
        return {
          title: null,
          content: null,
          footer: null,
          titleIcon: null,
          rightHeading: null,
        };
    }
  };

  const getInitialParts = () => {
    const onStep2Close = () => {
      onClose();
    };
    const onStep2Review = () => {
      dispatch(uiTriggered({ type: UI_ON_FINISH_AND_REVIEW_BUTTON_CLICK }));
      setStatus(Status.CONFIRM);
    };
    const onStep2Finish = () => {
      dispatch(uiTriggered({ type: UI_ON_FINISH_BUTTON_CLICK }));
      setStatus(Status.CONFIRM);
    };
    const handleFieldChange = ({
      fieldKey,
      fieldValue,
      isDateTimeCombinationValid,
    }: {
      fieldKey: keyof EditOrderForm;
      fieldValue: any;
      isDateTimeCombinationValid: boolean;
    }) => {
      const updated = {
        ...orderForm,
        [fieldKey]: fieldValue,
      };
      if (typeof isDateTimeCombinationValid !== 'undefined') {
        updated.isDateTimeCombinationValid = isDateTimeCombinationValid;
      }
      setOrderForm(updated);
    };
    const title = `Modify Order`;
    const rightHeading = (
      <Grid container justify="flex-end">
        <Grid item>
          <Button className={submittable ? classes.finishButton : null} variant="contained" disabled={!submittable} color="primary" onClick={onStep2Finish}>
            {`Finish`}
          </Button>
        </Grid>
      </Grid>
    );
    const content = (
      <EditOrderStep2
        order={order}
        orderForm={orderForm}
        onFieldChange={handleFieldChange}
        setSamePhoneNumber={setSamePhoneNumber}
        setPhoneNumberValid={setPhoneNumberValid}
        setPurchaseOrderRequired={setPurchaseOrderRequired}
      />
    );
    const footer = (
      <Grid className={classes.textAlignRight} container justify="flex-end" spacing={2}>
        <Grid className={classes.flexGrow1} item>
          <Button className={classes.button} color="primary" onClick={onStep2Close} variant="outlined">
            {`Cancel`}
          </Button>
        </Grid>
        <Grid item>
          <Button className={submittable ? classes.finishButton : null} disabled={!submittable} color="primary" onClick={onStep2Review} variant="contained">
            {`Finish & Review`}
          </Button>
        </Grid>
      </Grid>
    );
    return { title, content, footer, rightHeading };
  };
  const getConfirmParts = () => {
    const onStep3Back = () => {
      dispatch(uiTriggered({ type: UI_ON_BACK_BUTTON_CLICK }));
      setStatus(Status.INITIAL);
    };
    const onStep3Submit = async () => {
      dispatch(uiTriggered({ type: UI_ON_SUBMIT_BUTTON_CLICK }));
      const payload: UpdateOrderServicePayload = {
        dateTime: null,
        quantity: null,
        instructions: null,
        siteContactPhoneNumber: null,
        purchaseOrder: null,
      };
      if ((isStartTimeChanging || isStartDateChanging) && orderForm.startTime) {
        payload.dateTime = {
          offset: -new Date().getTimezoneOffset(),
          details: getDateTimeDetails(orderForm.startTime),
        };
      }
      if (isQuantityChanging) {
        payload.quantity = orderForm.quantity;
      }
      if (isInstructionsChanging) {
        payload.instructions = orderForm.instructions;
      }
      if (isSiteTelChanging) {
        payload.siteContactPhoneNumber = orderForm.siteTel;
      }
      if (isPurchaseOrderChanging) {
        payload.purchaseOrder = orderForm.purchaseOrder;
      }

      try {
        setStatus(Status.PROCESSING);
        if (isStartDateChanging) {
          // place duplicate order
          const accounts = await services.orderService.getPlaceOrderAccounts();
          const account = accounts.find((acc: any) => acc.id.trim() === order.summary.customerCode);
          let project = null;
          if (account && account.projects && account.projects.length > 0) {
            project = account.projects.find((project: any) => project.name === order.details.projectName);
          } else {
            account.projects = null;
          }

          const locations = (await services.hereMapsService.getAddressSuggestions({ keywords: order.details.deliveryAddress }).toPromise()).items;
          const locationDetails = locations && locations.length > 0 ? locations[0] : null;
          if (locationDetails === null) {
            throw new Error("Location details not found");
          }
          const coordsDetails = await services.hereMapsService.getCoordinates({ id: locationDetails.id }).toPromise();
          const latitude = coordsDetails.position.lat;
          const longitude = coordsDetails.position.lng;

          const address = `${locationDetails.address.houseNumber ?? ``}${locationDetails.address.street ? ` ${locationDetails.address.street}` : ``}${
            locationDetails.address.district ? `, ${locationDetails.address.district}` : ``}${
            locationDetails.address.city && !locationDetails.address.district ? `, ${locationDetails.address.city}` : ``}${
            locationDetails.address.stateCode ? `, ${locationDetails.address.stateCode}` : ``}${
            locationDetails.address.countryName ? `, ${locationDetails.address.countryName}` : ``} `.replace(/^,\s/, ``);

          const location = {
            address,
            coords: { latitude, longitude },
            details: locationDetails.address,
          };

          const productCode = order.details.product?.[0]?.code;
          if (!productCode) {
            throw new Error("Product code not found");
          }

          const slump = Number(order.details.slump);

          const availableProducts = await services.orderService.getProducts({
            projectId: project?.id,
            locationCode: order.summary.customerCode,
            latitude,
            longitude,
          });
          let product = availableProducts.find((p: any) => p.code === productCode);
          if (!product) {
            product = {
              description: order.details.product?.[0]?.name,
              code: productCode,
              category: "RECENT",
              slump,
            };
          }

          const driverInstructions = payload.instructions ?? orderForm.instructions;
          let siteContactName = order.details.siteContactName;
          if (!siteContactName) {
            if (driverInstructions) {
              const siteContactDetails = driverInstructions.match(/Site Contact: (\w+(?: \w+)*) \d{10}/);
              siteContactName = siteContactDetails && siteContactDetails.length ? siteContactDetails[1] : "";
            } else {
              siteContactName = "";
            }
          }

          let truckSpacing = null;
          if (order.details.spacing && typeof order.details.spacing === `string` && order.details.spacing.endsWith(` min`)) {
            truckSpacing = Number(order.details.spacing.replace(` min`, ``));
          }

          const placeDuplicateOrderPayload: any = {
            accountProject: {
              account,
              project,
            },
            dateTime: payload.dateTime,
            driverInstructions: ("" + driverInstructions).replace(/Site Contact: .*/, ""),
            location,
            lotNumber: order.details.lotNumber,
            orderStatusConfirmed: order.summary.status === ORDER_STATUS_CONFIRMED,
            plusLoad: order.details.plusLoad,
            pourMethod: order.details.pourMethod,
            product,
            purchaseOrder: payload.purchaseOrder ?? orderForm.purchaseOrder,
            quantity: payload.quantity ?? orderForm.quantity,
            siteContactName,
            siteContactPhoneNumber: payload.siteContactPhoneNumber ?? orderForm.siteTel,
            slump,
            truckSpacing,
          };

          if (!order.details.testingRequired || !driverInstructions) {
            placeDuplicateOrderPayload.testRequirements = "NO_TESTING_REQUIRED";
          } else {
            Object.assign(placeDuplicateOrderPayload, getTestDetailsFromInstructions(driverInstructions));
          }

          if (order.details.truckSize && order.details.truckSize !== "any") {
            placeDuplicateOrderPayload.truckSize = order.details.truckSize;
          }

          const responseOrderId = (await services.orderService.placeOrder(placeDuplicateOrderPayload)).response.orderId;
          // console.log('Placed duplicate Order:', responseOrderId);
          const newOrderKey = `${moment(payload.dateTime?.details).format("YYYYMMDD")}-${responseOrderId}-${order.summary.orderLine}`;

          // cancel existing order
          const date = order.summary.startTime;
          try {
            await services.orderService.cancelOrder({
              companyCode: order.details.companyCode,
              date: dateOnly(date, ORDER_API_DATE_FORMAT),
              orderCode: order.summary.orderCode,
              orderKey: order.orderKey,
              timezone: getTimezoneOffset(date),
            });
            setNewOrderKey(newOrderKey);
          } catch (err) {
            // cancel order api can return error but may still cancel order
            let orderStatus = '';
            for (let i = 0; i < 6; i++) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise((resolve) => setTimeout(resolve, 10000)); // wait 10 seconds

              // eslint-disable-next-line no-await-in-loop
              const orderDetails = await services.orderService.getOrderDetails({
                date: dateOnly(date, ORDER_API_DATE_FORMAT),
                orderCode: order.summary.orderCode,
                orderLine: order.summary.orderLine,
              });
              orderStatus = orderDetails?.summary?.status ?? '';
              if (orderStatus === ORDER_STATUS_CANCELLED || order.summary.status === ORDER_STATUS_CANCELLED) {
                break;
              }
            }

            if (orderStatus !== ORDER_STATUS_CANCELLED && order.summary.status !== ORDER_STATUS_CANCELLED) {
              await services.orderService.cancelOrder({
                companyCode: order.details.companyCode,
                date: dateOnly(orderForm.startTime!, ORDER_API_DATE_FORMAT),
                orderCode: responseOrderId,
                orderKey: newOrderKey,
                timezone: getTimezoneOffset(orderForm.startTime!),
              });
              throw err;
            }
            setNewOrderKey(newOrderKey);
          }
        } else {
          await services.orderService.updateOrder(payload, order.orderKey);
        }
        setStatus(Status.DONE);
      } catch (ex) {
        dispatch(openSnackbar({ message: SNACKBAR_GENERAL_ERROR_MESSAGE }));
        setStatus(Status.INITIAL);
      }
    };
    const title = `Review Modifications`;
    const content = (
      <React.Fragment>
        <EditOrderStep3 order={order} changingItems={changingItems} />
      </React.Fragment>
    );
    const footer = (
      <Grid className={classes.textAlignRight} container justify="flex-end" spacing={2}>
        <Grid className={classes.flexGrow1} item>
          <Button className={classes.button} color="primary" onClick={onStep3Back} variant="outlined">
            {`Back`}
          </Button>
        </Grid>
        <Grid item>
          <Button className={classes.button} color="primary" onClick={onStep3Submit} variant="contained">
            {`Submit Changes`}
          </Button>
        </Grid>
      </Grid>
    );
    return { title, content, footer, titleIcon: <ArrowBackIcon /> };
  };
  const getProgressingParts = () => {
    return {
      title: null,
      content: <PleaseWatiSpinner />,
      footer: null,
    };
  };

  const getDoneParts = () => {
    return {
      title: ' ',
      content: (
        <ProgressDone
          title={`Your changes have been submitted.`}
          stepContents={[
            `Your changes will be applied to your order within 5 minutes.`,
            `All changes are open to additional review by Boral for up to 1 business day.`,
            `We will call you if there's a problem.`,
          ]}
        />
      ),
      footer: (
        <Grid className={classes.textAlignRight} container justify="flex-end" spacing={2}>
          <Grid className={classes.flexGrow1} item>
            <Button className={classes.button} onClick={onGoToOrders} variant="contained">
              {`Go to all orders`}
            </Button>
          </Grid>
          <Grid item>
            <Button className={classes.button} color="primary" onClick={() => onGoToThisOrder(newOrderKey)} variant="contained">
              {`Go to this order`}
            </Button>
          </Grid>
        </Grid>
      ),
    };
  };

  if (status === Status.ALERT) {
    const onStep1Close = () => {
      onClose();
    };
    const onStep1Continue = () => {
      setStatus(Status.INITIAL);
    };
    return <ModifyingInfoModal open={open} onClose={onStep1Close} onContinue={onStep1Continue} order={order} />;
  }

  const handleTitleIconClick = () => {
    if (status === Status.CONFIRM) {
      dispatch(uiTriggered({ type: UI_ON_BACK_ARROW_BUTTON_CLICK }));
      setStatus(Status.INITIAL);
    }
  };
  const { title, content, footer, titleIcon = null, rightHeading = null } = getParts();

  const onIconClick = status !== Status.DONE && status !== Status.INITIAL && handleTitleIconClick;

  return (
    <ModalComponent
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={onClose}
      rightHeading={rightHeading}
      dialogContentClasses={classes.modalContent}
      dialogTitleRootClasses={(status === Status.INITIAL || status === Status.CONFIRM) && classes.modalTitleWithDivider}
      title={title}
      onIconClick={onIconClick || null}
      icon={titleIcon}
      content={content}
      footer={footer}
      paperScrollPaper={false}
    />
  );
};

const EditOrderModal = withStyles(themeCreateInjectableStyles(EditOrderModalStyles) as any, { withTheme: true })(EditOrderModalComp);

export default EditOrderModal;
